import { HttpResponse } from '@angular/common/http';
import { Component, DestroyRef, Input, OnInit, Type, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  EMPTY,
  from,
  isObservable,
  Observable,
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
  timer
} from 'rxjs';
import { AuthService } from '../../../../services/auth/auth.service';
import {
  SalesInsightsComponentDefinition,
  SalesInsightsDashboardDefinition,
  SalesInsightsSection,
  SalesInsightsStatus
} from '../../models/sales-insights.model';
import { SalesInsightsActivityService } from '../../services/sales-insights-activity.service';
import { SalesInsightsComponentRegistry } from '../../services/sales-insights-component-registry.service';
import { SalesInsightsDashboardState } from '../../services/sales-insights-dashboard-state.service';
import {
  SALES_INSIGHTS_PAGE_ACTIONS,
  SalesInsightsPageAction,
  SalesInsightsPageActionContext
} from '../../services/sales-insights-page-actions';
import { SalesInsightsService } from '../../services/sales-insights.service';
import { salesInsightsSaveResponse } from '../../utils/sales-insights-download';
import { salesInsightsExportRequest } from '../../utils/sales-insights-export';
import { SalesInsightsActivityTypesComponent } from '../sales-insights-activity-types/sales-insights-activity-types.component';
import { SalesInsightsBaseComponent } from '../sales-insights-base/sales-insights-base.component';

export const SALES_INSIGHTS_ACTION_EXPORT = 'export';
export const SALES_INSIGHTS_ACTION_REFRESH = 'refresh';
export const SALES_INSIGHTS_ACTION_BUDGET_REPORT = 'budget-report';
export const SALES_INSIGHTS_ACTION_ACTIVITY_TYPES = 'activity-types';

const STATUS_POLL_MS = 2000;
const GRID_COLUMNS = 12;
const HALF_ROW = 6;
const NARROW_COMPONENT_MAX_WIDTH = 4;

@Component({
  selector: 'lib-sales-insights-dashboard',
  standalone: false,
  templateUrl: './sales-insights-dashboard.component.html',
  styleUrl: './sales-insights-dashboard.component.scss',
  providers: [SalesInsightsDashboardState]
})
export class SalesInsightsDashboardComponent implements OnInit {
  @Input() dashboardId?: string;

  protected salesInsightsService = inject(SalesInsightsService);
  protected registry = inject(SalesInsightsComponentRegistry);
  protected dashboardState = inject(SalesInsightsDashboardState);
  protected authService = inject(AuthService);
  protected route = inject(ActivatedRoute);
  protected translate = inject(TranslateService);
  protected ngbModal = inject(NgbModal);
  protected activityService = inject(SalesInsightsActivityService);
  private destroyRef = inject(DestroyRef);
  private extraPageActions = (inject(SALES_INSIGHTS_PAGE_ACTIONS, { optional: true }) || []).flat();

  definition: SalesInsightsDashboardDefinition | null = null;
  loading = true;
  error: string | null = null;
  status: SalesInsightsStatus | null = null;
  refreshing = false;
  refreshError: string | null = null;
  isAdmin = false;
  pageActions: SalesInsightsPageAction[] = [];
  runningActions = new Set<string>();
  actionError: string | null = null;
  activeTabs: { [section: number]: number } = {};

  ngOnInit(): void {
    this.authService
      .getRoles()
      .pipe(
        filter(roles => !!roles),
        first()
      )
      .subscribe(roles => {
        this.isAdmin = roles.includes('ADMIN');
        this.pageActions = this.resolvePageActions(this.definition);
      });

    this.dashboardIds()
      .pipe(
        tap(() => {
          this.loading = true;
          this.error = null;
        }),
        switchMap(id =>
          this.salesInsightsService.getDashboard(id).pipe(
            catchError(err => {
              this.error = err?.error?.message || 'SALES_INSIGHTS_DASHBOARD_LOAD_FAILED';
              return of(null);
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(definition => {
        this.dashboardState.setFilters({});
        this.dashboardState.setCustomerHealthFlag(null);
        this.dashboardState.hasCustomerList = (definition?.sections || []).some(section =>
          (section.components || []).some(component => component.type === 'customer-health')
        );
        this.definition = definition;
        this.activeTabs = {};
        this.pageActions = this.resolvePageActions(definition);
        this.actionError = null;
        this.loading = false;
      });

    this.loadStatus();
  }

  componentFor(definition: SalesInsightsComponentDefinition): Type<SalesInsightsBaseComponent<any>> | null {
    return this.registry.get(definition.type);
  }

  get updatedFormat(): string {
    const computedAt = this.status?.computedAt ? new Date(this.status.computedAt) : null;
    return computedAt && computedAt.toDateString() !== new Date().toDateString() ? 'd MMM HH:mm' : 'HH:mm';
  }

  // Tablets get two narrow components per row and give anything wider the whole row; the configured widths
  // apply from xl, where a quarter of the row still fits a KPI value.
  columnClasses(definition: SalesInsightsComponentDefinition): string {
    const width = Math.min(Math.max(Math.round(definition.width || GRID_COLUMNS), 1), GRID_COLUMNS);
    const tabletWidth = width <= NARROW_COMPONENT_MAX_WIDTH ? HALF_ROW : GRID_COLUMNS;
    return `col-12 col-md-${tabletWidth} col-xl-${width}`;
  }

  isTabs(section: SalesInsightsSection): boolean {
    return section.layout === 'tabs';
  }

  refresh(): void {
    this.followRefresh(this.salesInsightsService.refresh());
  }

  isRunning(action: SalesInsightsPageAction): boolean {
    return action.action === SALES_INSIGHTS_ACTION_REFRESH ? this.refreshing : this.runningActions.has(action.action);
  }

  runAction(action: SalesInsightsPageAction): void {
    const definition = this.definition;
    if (!definition || this.isRunning(action)) {
      return;
    }
    const context: SalesInsightsPageActionContext = {
      definition,
      filters: this.dashboardState.filters,
      isAdmin: this.isAdmin,
      reload: () => this.dashboardState.reload()
    };
    const result = action.run(context);
    if (!isObservable(result) && !(result instanceof Promise)) {
      return;
    }
    this.actionError = null;
    this.runningActions.add(action.action);
    (isObservable(result) ? result : from(result))
      .pipe(
        catchError(err => {
          this.actionError = err?.error?.message || 'SALES_INSIGHTS_ACTION_FAILED';
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({ complete: () => this.runningActions.delete(action.action) });
  }

  protected standardPageActions(): SalesInsightsPageAction[] {
    return [
      {
        action: SALES_INSIGHTS_ACTION_EXPORT,
        labelKey: 'SALES_INSIGHTS_EXPORT_EXCEL',
        icon: 'fa-solid fa-file-excel',
        run: context =>
          this.salesInsightsService
            .exportDashboard(
              context.definition.id,
              salesInsightsExportRequest(
                context.definition,
                this.salesInsightsService.exportFilters(context.filters),
                key => this.translate.instant(key)
              )
            )
            .pipe(tap((response: HttpResponse<Blob>) => salesInsightsSaveResponse(response, `${context.definition.id}.xlsx`)))
      },
      {
        action: SALES_INSIGHTS_ACTION_REFRESH,
        labelKey: 'SALES_INSIGHTS_RECALCULATE',
        icon: 'fa-solid fa-rotate',
        adminOnly: true,
        run: () => this.refresh()
      },
      {
        action: SALES_INSIGHTS_ACTION_ACTIVITY_TYPES,
        labelKey: 'SALES_INSIGHTS_ACTIVITY_TYPES',
        icon: 'fa-solid fa-tags',
        adminOnly: true,
        run: () => this.openActivityTypes()
      },
      {
        action: SALES_INSIGHTS_ACTION_BUDGET_REPORT,
        labelKey: 'SALES_INSIGHTS_BUDGET_REPORT',
        icon: 'fa-solid fa-chart-column',
        run: context => {
          const year = new Date().getFullYear();
          return this.salesInsightsService
            .getBudgetReport(year, context.filters.salesperson)
            .pipe(tap((response: HttpResponse<Blob>) => salesInsightsSaveResponse(response, `budget-${year}.xlsx`)));
        }
      }
    ];
  }

  // The page follows every saved change while the panel is open, so the colours and visit numbers are current.
  protected openActivityTypes(): void {
    const ref = this.ngbModal.open(SalesInsightsActivityTypesComponent, {
      size: 'lg',
      centered: true,
      scrollable: true
    });
    const instance = ref.componentInstance as SalesInsightsActivityTypesComponent;
    instance.changed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(metadataChanged => {
      this.activityService.reloadTypes(metadataChanged);
      this.dashboardState.notifyChanged('activityTypes');
    });
    ref.result.catch(() => {});
  }

  // Without configured actions the header keeps the recompute button for admins, as before actions existed.
  private resolvePageActions(definition: SalesInsightsDashboardDefinition | null): SalesInsightsPageAction[] {
    if (!definition) {
      return [];
    }
    const available = new Map<string, SalesInsightsPageAction>();
    [...this.standardPageActions(), ...this.extraPageActions].forEach(action => available.set(action.action, action));
    const names = definition.actions?.length ? definition.actions : [SALES_INSIGHTS_ACTION_REFRESH];
    return names
      .map(name => available.get(name))
      .filter((action): action is SalesInsightsPageAction => !!action && (!action.adminOnly || this.isAdmin));
  }

  private followRefresh(start$: Observable<SalesInsightsStatus>): void {
    this.refreshing = true;
    this.refreshError = null;
    start$
      .pipe(
        switchMap(status => (status?.running ? this.pollUntilDone() : of(status))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: status => {
          this.status = status;
          this.refreshing = false;
          this.refreshError = status?.lastError || null;
          this.dashboardState.reload();
        },
        error: err => {
          this.refreshing = false;
          this.refreshError = err?.error?.message || 'SALES_INSIGHTS_REFRESH_FAILED';
        }
      });
  }

  private pollUntilDone(): Observable<SalesInsightsStatus> {
    return timer(STATUS_POLL_MS, STATUS_POLL_MS).pipe(
      switchMap(() => this.salesInsightsService.getStatus()),
      tap(status => (this.status = status)),
      takeWhile(status => status.running, true),
      filter(status => !status.running)
    );
  }

  private loadStatus(): void {
    this.salesInsightsService
      .getStatus()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(status => {
        this.status = status;
        if (status?.running) {
          this.followRefresh(of(status));
        }
      });
  }

  private dashboardIds(): Observable<string> {
    if (this.dashboardId) {
      return of(this.dashboardId);
    }
    return this.route.paramMap.pipe(
      map(params => params.get('id')),
      filter((id): id is string => !!id),
      distinctUntilChanged()
    );
  }
}
